import * as React from 'react';
import styled, { css } from 'styled-components';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import { FormattedMessage } from 'react-intl';
import { getBiddingInfo } from '@/redux/modules/biddingInfo';
import { getBuyNow } from '@/redux/modules/buyNow';
import { getCatalog } from '@/redux/modules/catalog';
import { getItem } from '@/redux/modules/item';
import { getItemUrl } from '@/utils/urls';
import { getLiveItemData } from '@/redux/modules/console';
import { getLiveItemStatus } from '@/redux/modules/liveItemStatus';
import isEqual from 'react-fast-compare';
import ItemPriceGatewayContainer from '@/components/ItemPriceGateway/ItemPriceGatewayContainer';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

type Props = {
    fullWidth?: boolean;
    itemId: number;
};

const PriceRow = ({ fullWidth = true, itemId }: Props) => {
    const { push } = useHistory();
    const { price: buyNowPrice, status: buyNowStatus } = useSelector((state) => getBuyNow(state, itemId), isEqual);
    const { catalogId, startPrice, title } = useSelector((state) => getItem(state, itemId), isEqual);
    const { catalogStatus, currency = 'usd', isCatalogOnly } = useSelector(
        (state) => getCatalog(state, catalogId),
        isEqual
    );
    const { bidCount = 0 } = useSelector((state) => getLiveItemData(state, itemId), isEqual);
    const { open: isLotOpen } = useSelector((state) => getLiveItemStatus(state, itemId), isEqual);

    const { bidActivityCount, isAvailable, isLocked, isPassed, isSold, leadingBid, salePrice = 0 } = useSelector(
        (state) => getBiddingInfo(state, itemId),
        isEqual
    );

    const buyNowAvailable = buyNowStatus === 'available' && catalogStatus === 'done';
    const isUnsold = isLocked && !isPassed && !isSold && !isLotOpen;
    const currentPrice = leadingBid || salePrice || startPrice || 0;
    const numberOfBids = 0 + Number(bidCount) + Number(bidActivityCount);

    return (
        <WrapperText h6 useFullWidthStyle={fullWidth}>
            {isLotOpen ? (
                <LiveItemCurrentBidContainerDiv>
                    <ItemLiveBidDiv>
                        <BidAmountText primary>
                            <FormattedCurrency convert noFraction sourceCurrency={currency} value={currentPrice} />
                        </BidAmountText>
                        <BidCountText colorPrimaryWeak secondary>
                            {numberOfBids === 0 ? null : numberOfBids === 1 ? (
                                <FormattedMessage id="oneBid" />
                            ) : (
                                <FormattedMessage
                                    id="bids_with_count"
                                    values={{ numberOfBids, s: numberOfBids === 1 ? '' : 's' }}
                                />
                            )}
                        </BidCountText>
                    </ItemLiveBidDiv>
                </LiveItemCurrentBidContainerDiv>
            ) : buyNowAvailable ? (
                <CurrentPriceText>
                    <StyledPriceRowSpan>
                        <FormattedMessage
                            id="buy_now_for"
                            values={{
                                price: <StyledPriceRowCurrency sourceCurrency={currency} value={buyNowPrice} />,
                            }}
                        />
                    </StyledPriceRowSpan>
                </CurrentPriceText>
            ) : isAvailable && !isUnsold && !isCatalogOnly ? (
                <StyledPriceRowSpan>
                    <AbsenteeItemCurrentBidText>
                        <FormattedCurrency convert noFraction sourceCurrency={currency} value={currentPrice} />
                    </AbsenteeItemCurrentBidText>
                    <Text colorPrimaryWeak secondary>
                        {bidActivityCount === 0 ? null : bidActivityCount === 1 ? (
                            <FormattedMessage id="oneBid" />
                        ) : (
                            <FormattedMessage
                                id="bids_with_count"
                                values={{ numberOfBids: bidActivityCount, s: bidActivityCount === 1 ? '' : 's' }}
                            />
                        )}
                    </Text>
                </StyledPriceRowSpan>
            ) : (
                <StyledPriceRowSpan>
                    {isSold ? (
                        <StyledItemPriceGatewayContainer
                            itemId={itemId}
                            runFunction={() => {
                                push({ pathname: getItemUrl(itemId, title) });
                            }}
                        >
                            <FormattedMessage
                                id="sold_for"
                                values={{
                                    amount: (
                                        <StyledPriceRowCurrency
                                            noFraction
                                            sourceCurrency={currency}
                                            value={salePrice}
                                        />
                                    ),
                                }}
                            />
                        </StyledItemPriceGatewayContainer>
                    ) : isPassed ? (
                        <StyledItemPriceGatewayContainer itemId={itemId}>
                            {isCatalogOnly ? (
                                <FormattedMessage id="lot_closed" />
                            ) : (
                                <FormattedMessage id="lot_passed" />
                            )}
                        </StyledItemPriceGatewayContainer>
                    ) : isUnsold ? (
                        <StyledItemPriceGatewayContainer itemId={itemId}>
                            <FormattedMessage id="lot_unsold_pending" />
                        </StyledItemPriceGatewayContainer>
                    ) : null}
                </StyledPriceRowSpan>
            )}
        </WrapperText>
    );
};

export default React.memo<Props>(PriceRow);

const WrapperFullWidthStyle = css`
    &&& {
        width: 100%;
        margin-top: 3px;
    }
`;

const WrapperText = styled(Text)<{ useFullWidthStyle: boolean }>`
    ${(props) => props.useFullWidthStyle && WrapperFullWidthStyle}
    display: inline-block;
    letter-spacing: 1px;
    line-height: 17px;
    margin-top: 4px;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        font-size: 16px;
        line-height: 19px;
        margin-top: 3px;
    }
`;

WrapperText.displayName = 'WrapperText';

const LiveItemCurrentBidContainerDiv = styled.div`
    display: flex;
    flex-direction: column;

    > * {
        flex: 1 1 50%;
    }
`;

const StyledPriceRowSpan = styled.span`
    display: flex;
`;

const StyledPriceRowCurrency = styled(FormattedCurrency)`
    margin-left: 5px;
`;

const ItemLiveBidDiv = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: 5px;
`;

const BidAmountText = styled(Text)`
    font-size: 16px;
    margin-right: 8px;
`;

const BidCountText = styled(Text)`
    letter-spacing: 1px;
`;

// cant have an h5 as a child of h6
const AbsenteeItemCurrentBidText = styled(Text)`
    margin-right: 8px;
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
    font-size: 16px;
    line-height: 22px;
`;
// cant have an h5 as a child of h6
const CurrentPriceText = styled(Text)`
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
    font-size: 16px;
    line-height: 22px;
`;

const StyledItemPriceGatewayContainer = styled(ItemPriceGatewayContainer)`
    font-size: 12px;
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
    height: 100%;
    letter-spacing: 0.5px;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        font-size: 14px;
    }
`;
